import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files
import enUsTranslationsBase from './locales/en-us/translation.json';
import deDeTranslationsBase from './locales/de-de/translation.json';
import esEsTranslationsBase from './locales/es-es/translation.json';
import deDeCampaignMission1 from './locales/de-de/campaignMissions/chapter_1_mission_1.json';
import deDeCampaignMission2 from './locales/de-de/campaignMissions/chapter_1_mission_2.json';
import deDeCampaignMission3 from './locales/de-de/campaignMissions/chapter_1_mission_3.json';
import deDeCampaignMission4 from './locales/de-de/campaignMissions/chapter_1_mission_4.json';
import esEsCampaignMission1 from './locales/es-es/campaignMissions/chapter_1_mission_1.json';
import esEsCampaignMission2 from './locales/es-es/campaignMissions/chapter_1_mission_2.json';
import esEsCampaignMission3 from './locales/es-es/campaignMissions/chapter_1_mission_3.json';
import esEsCampaignMission4 from './locales/es-es/campaignMissions/chapter_1_mission_4.json';
import {
  EMOTE_SPEECH_LINES_EN,
  EMOTE_SPEECH_LINES_DE,
  EMOTE_SPEECH_LINES_ES,
} from './locales/emoteSpeechCatalog.js';

function injectEmoteSpeechLines(base, lines) {
  const profile = base.profile || {};
  const emotes = profile.emotes || {};
  return {
    ...base,
    profile: {
      ...profile,
      emotes: {
        ...emotes,
        lines: {
          ...(emotes.lines || {}),
          ...lines,
        },
      },
    },
  };
}

const deDeCampaignMissions = {
  ...deDeCampaignMission1,
  ...deDeCampaignMission2,
  ...deDeCampaignMission3,
  ...deDeCampaignMission4,
};

const esEsCampaignMissions = {
  ...esEsCampaignMission1,
  ...esEsCampaignMission2,
  ...esEsCampaignMission3,
  ...esEsCampaignMission4,
};

const deDeWithEmotes = injectEmoteSpeechLines(deDeTranslationsBase, EMOTE_SPEECH_LINES_DE);
const deDeTranslations = {
  ...deDeWithEmotes,
  campaign: {
    ...deDeWithEmotes.campaign,
    missions: deDeCampaignMissions,
  },
};

const esEsWithEmotes = injectEmoteSpeechLines(esEsTranslationsBase, EMOTE_SPEECH_LINES_ES);
const esEsTranslations = {
  ...esEsWithEmotes,
  campaign: {
    ...esEsWithEmotes.campaign,
    missions: esEsCampaignMissions,
  },
};

const enUsTranslations = injectEmoteSpeechLines(enUsTranslationsBase, EMOTE_SPEECH_LINES_EN);

// Debug: Verify imports
console.log('i18n imports check:', {
  enUs: !!enUsTranslations,
  enUsStrategy: !!enUsTranslations?.strategy,
  deDe: !!deDeTranslations,
  esEs: !!esEsTranslations,
  enUsSample: enUsTranslations?.strategy?.prepareStrategy,
  enUsFull: enUsTranslations
});

// Get saved language safely and convert to normalized format
const getSavedLanguage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('i18nextLng');
      if (saved) {
        // Convert to normalized format that i18next uses (en-us -> en-US)
        if (saved === 'en-us') return 'en-US';
        if (saved === 'de-de') return 'de-DE';
        if (saved === 'es-es') return 'es-ES';
        return saved; // Return as-is if already normalized
      }
      return 'en-US'; // Use normalized format
    }
  } catch (e) {
    console.warn('localStorage not available:', e);
  }
  return 'en-US'; // Use normalized format
};

const savedLanguage = getSavedLanguage();

// Initialize i18n - this must complete before components use it
const initI18n = () => {
  if (i18n.isInitialized) {
    console.log('i18n already initialized');
    return i18n;
  }

  console.log('i18n initializing with language:', savedLanguage);
  console.log('i18n resources structure:', {
    'en-us': !!enUsTranslations,
    'de-de': !!deDeTranslations,
    'es-es': !!esEsTranslations
  });

  i18n
    .use(initReactI18next)
    .init({
      lng: savedLanguage,
      fallbackLng: 'en-US',
      debug: false,
      interpolation: {
        escapeValue: false
      },
      react: {
        useSuspense: false
      }
    })
    .then(() => {
      // Add resources for all languages using normalized format (en-US, de-DE, es-ES)
      // This ensures resources are available regardless of which format is used
      i18n.addResourceBundle('en-US', 'translation', enUsTranslations, true, true);
      i18n.addResourceBundle('de-DE', 'translation', deDeTranslations, true, true);
      i18n.addResourceBundle('es-ES', 'translation', esEsTranslations, true, true);
      
      // Also add with lowercase versions for compatibility
      i18n.addResourceBundle('en-us', 'translation', enUsTranslations, true, true);
      i18n.addResourceBundle('de-de', 'translation', deDeTranslations, true, true);
      i18n.addResourceBundle('es-es', 'translation', esEsTranslations, true, true);
      
      const normalizedLang = i18n.language; // This will be the normalized version
      
      console.log('i18n initialization complete:', {
        language: i18n.language,
        normalizedLang: normalizedLang,
        isInitialized: i18n.isInitialized,
        storeDataKeys: i18n.store?.data ? Object.keys(i18n.store.data) : [],
        currentLangData: i18n.store?.data?.[normalizedLang],
        testTranslation: i18n.t('strategy.prepareStrategy')
      });
    })
    .catch((err) => {
      console.error('i18n initialization error:', err);
    });

  return i18n;
};

// Initialize immediately
initI18n();

export default i18n;

